import React, { ReactNode } from 'react';
import { useRevealAnimation } from '@/hooks/useRevealAnimation';
import { MaterialIcon } from '@/components/common/MaterialIcon';

type KeyBulletPoint = {
  icon?: string;
  title?: ReactNode;
  text?: ReactNode;
};

type KeyBulletListProps = {
  heading: ReactNode;
  description?: ReactNode;
  points: KeyBulletPoint[];
};

export const KeyBulletList: React.FC<KeyBulletListProps> = ({
  heading,
  description,
  points,
}) => {
  const { ref, isVisible } = useRevealAnimation();

  return (
    <div
      ref={ref}
      className={`my-24 transform transition-all duration-700 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-24">
        {/* Heading Column - left side */}
        <div className="md:col-span-2">
          <h3 className="text-3xl font-light text-zinc-900 dark:text-white">{heading}</h3>
        </div>

        {/* Content Column - right side */}
        <div className="md:col-span-3">
          {/* Description at the top of the right column */}
          {description && (
            <p className="text-zinc-500 dark:text-zinc-400 text-base mb-12">
              {description}
            </p>
          )}

          {/* Bullet points as minimal items */}
          <div className="space-y-10">
            {points.map((point, index) => (
              <div
                key={index}
                className="border-t border-zinc-200 dark:border-zinc-700 pt-6"
              >
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-12 h-12 flex items-center justify-center mr-5 bg-zinc-100 dark:bg-zinc-800 rounded-full">
                    <MaterialIcon
                      icon={point.icon || 'check_circle'}
                      size={32}
                      fill={true}
                      className="text-zinc-700 dark:text-zinc-300"
                    />
                  </span>
                  <div>
                    {(point.title || point.text) && (
                      <h4 className="text-lg font-light text-zinc-800 dark:text-zinc-200 mb-2 leading-tight">
                        {point.title ||
                          (typeof point.text === 'string'
                            ? point.text.split(' ').slice(0, 4).join(' ')
                            : '')}
                      </h4>
                    )}
                    {point.text && (
                      <p className="text-zinc-600 dark:text-zinc-400 text-base leading-relaxed">
                        {point.text}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
